import { z } from 'zod';

import { buildQueryString } from '../../utils/querystring';
import {
  makeEndpoint,
  MinimalHostSchema,
  paginateQuerySchema,
  paginateResponseSchema,
  REST_API_ENTITY,
  sortOptions,
} from '../common';
import { IMediaReferenceSchema } from '../media/media';

export const BrandsFinancialSummarySchema = z
  .object({
    brandId: z.string(),
    currency: z.string(),
    host: MinimalHostSchema.optional(),
    experience: z
      .object({
        id: z.string(),
        title: z.string(),
        coverPicture: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    resourceGroup: z
      .object({
        id: z.string(),
        title: z.string(),
        coverPicture: IMediaReferenceSchema.nullable(),
        resourceGroupCollectionId: z.string(),
      })
      .optional(),
    brand: z
      .object({
        id: z.string(),
        name: z.string(),
        logo: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    grossRevenue: z.number(),
    netProfit: z.number(),
    totalHostEarning: z.number(),
    averageTransactionValue: z.number(),
    bookingCount: z.number(),
  })
  .strict();

export const BrandsFinancialSummaryResponseSchema = paginateResponseSchema(BrandsFinancialSummarySchema).strict();

export const financialSummaryGroupingOptions = ['experience', 'collection', 'listing', 'host', 'brand'] as const;

export const GetBrandsFinancialSummaryQuerySchema = paginateQuerySchema({
  sortBy: ['grossRevenue', 'netProfit', 'totalHostEarning', 'averageTransactionValue'] as const,
})
  .omit({ sortDir: true, filters: true, q: true })
  .extend({
    sortDir: z.enum(sortOptions).optional(),
    experienceId: z.string().uuid().optional(),
    resourceGroupCollectionId: z.string().uuid().optional(),
    groupBy: z.enum(financialSummaryGroupingOptions),
  });

export const GetBrandsFinancialSummaryEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    entityType,
    entityId,
    query,
  }: {
    entityType: REST_API_ENTITY;
    entityId: string;
    query: z.input<typeof GetBrandsFinancialSummaryQuerySchema>;
  }) => `/v1/${entityType}/${entityId}/financial-summary?${buildQueryString(query)}`,
  responseSchema: BrandsFinancialSummaryResponseSchema,
  requestSchema: z.void(),
  querySchema: GetBrandsFinancialSummaryQuerySchema,
});
